import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { CpuIcon, ShieldCheckIcon, LeafIcon, DatabaseIcon } from 'lucide-react';
import { SlideLayout, itemVariants } from '../ui/SlideLayout';
const advantages = [{
  icon: CpuIcon,
  title: 'Workload-Shaped Hardware',
  description: 'The silicon itself is configured for your workload — not a general-purpose chip running at a fraction of its capacity. Every gate on the device is doing useful work for your model.',
  accent: 'Your hardware, your workload'
}, {
  icon: ShieldCheckIcon,
  title: 'Deterministic Latency',
  description: 'Every request lands in a tight, predictable band. No scheduling jitter, no tail-latency surprises. Your SLAs become reliable commitments, not hopeful targets.',
  accent: 'Reliable, not fragile'
}, {
  icon: LeafIcon,
  title: 'Efficiency That Becomes Cost Savings',
  description: 'When hardware adapts to the workload instead of the other way around, waste disappears. That efficiency flows directly into lower cost-per-token for your application.',
  accent: 'Less waste, lower cost'
}, {
  icon: DatabaseIcon,
  title: 'Custom Memory Hierarchies',
  description: 'Purpose-built memory paths optimized for the LLM decode phase — the part your users actually feel. Buffers sized to exact access patterns, with full bandwidth reserved for inference.',
  accent: 'Optimized for what matters'
}];
export function RealTimeSlide() {
  return <SlideLayout data-id="element-388">
      <motion.div variants={itemVariants} className="mb-16 text-center" data-id="element-389">
        <h2 className="text-4xl md:text-5xl font-bold text-brand-text leading-tight" data-id="element-390">
          FPGAs Do What{' '}
          <span className="text-brand-text-muted" data-id="element-391">GPUs Can't</span>
        </h2>
        <p className="text-lg text-brand-text-dim mt-4 max-w-2xl mx-auto" data-id="element-392">
          When the hardware reshapes itself around your workload, new
          capabilities emerge.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-393">
        {advantages.map((item, i) => <motion.div key={item.title} variants={itemVariants} className="bg-brand-bg-card border border-brand-border rounded-2xl p-8 relative overflow-hidden group" data-id="element-394">
            {/* Subtle corner glow */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-brand-teal/5 blur-3xl rounded-full pointer-events-none group-hover:bg-brand-teal/10 transition-colors duration-700" data-id="element-395" />

            <div className="relative z-10" data-id="element-396">
              <div className="flex items-center gap-4 mb-5" data-id="element-397">
                <div className="p-3 bg-brand-teal/10 rounded-xl border border-brand-teal/20" data-id="element-398">
                  <item.icon className="w-6 h-6 text-brand-teal" data-id="element-399" />
                </div>
                <span className="text-xs font-mono text-brand-teal tracking-wider uppercase" data-id="element-400">
                  {item.accent}
                </span>
              </div>

              <h3 className="text-2xl font-bold text-brand-text mb-3" data-id="element-401">
                {item.title}
              </h3>
              <p className="text-brand-text-dim leading-relaxed" data-id="element-402">
                {item.description}
              </p>
            </div>
          </motion.div>)}
      </div>
    </SlideLayout>;
}